import { Component, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Popover } from 'primeng/popover';
import readXlsxFile from 'read-excel-file';
import { firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { DocumentSupportImportModel } from 'src/app/models/imports/document-support-import.model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { CompanyService } from 'src/app/services/management/company.service';
import { EnterpriseService } from 'src/app/services/management/enterprise.service';
import { DocumentSupportService } from 'src/app/services/shopping/document-support.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import moment from 'moment';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { ArticleService } from 'src/app/services/archive/article.service';
import { HttpEventType } from '@angular/common/http';

@Component({
  selector: 'app-document-support-import',
  templateUrl: './document-support-import.component.html',
  styleUrls: ['./document-support-import.component.scss'],
  standalone: false
})
export class DocumentSupportImportComponent implements OnInit {

  private readonly articleService = inject(ArticleService);
  private readonly branchService = inject(BranchService);
  private readonly companyService = inject(CompanyService);
  private readonly enterpriseService = inject(EnterpriseService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly headerService = inject(HeaderService);
  private readonly documentSupportService = inject(DocumentSupportService);
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly windowService = inject(WindowService);

  @ViewChild('information') informationPopover!: Popover;
  branchCode: number = 0;
  branchId: number = 0;
  companyCode: number = 0;
  companyId: number = 0;
  documentSupportsAlerts: PreInvoicingAlertModel[] = [];
  enterpriseCode: number = 0;
  enterpriseId: number = 0;
  errorInvoice: number = 0;
  errorNotes: number = 0;
  eventFile: any;
  formImport: FormGroup = new FormGroup({});
  isOpened: string | undefined = undefined;
  listArticles: any[] = [];
  listBranches: any[] = [];
  listCompanies: any[] = [];
  listEnterprises: any[] = [];
  loadPreInvoicingModel: LoadPreInvoicingModel = new LoadPreInvoicingModel();
  nameFile: string = 'Ningún archivo seleccionado.';
  nameWindow: string = 'Importación Documentos Soporte';
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  url: string = "document-support-import";
  visibleTxt: boolean = false;

  async ngOnInit() {
    this.validateForm();
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    await this.getPermission();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  async ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  attach() {
    document.getElementById('fileImport')?.click();
  }

  clearInputFile() {
    this.nameFile = 'Ningún archivo seleccionado.';
    if (this.eventFile) {
      this.eventFile.target.value = '';
    }
  }

  downloadErrorsTxt() {
    if (!this.documentSupportsAlerts || this.documentSupportsAlerts.length === 0) {
      this.toastHandlerService.showToast(
        'error',
        'No hay alertas para exportar.'
      );
      return;
    }

    this.documentSupportService.exportImportErrors(this.documentSupportsAlerts).subscribe({
      next: (blob: Blob) => {
        const file = new Blob([blob], { type: 'text/plain' });
        const url = window.URL.createObjectURL(file);
        const today = new Date();
        const formatDate = today.toISOString().split('T')[0];

        const a = document.createElement('a');
        a.href = url;
        a.download = `ErroresImportacionDocumentosSoporte_${formatDate}.txt`;
        a.click();

        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastHandlerService.showToast(
          'error',
          'Error al exportar el archivo'
        );
      }
    });
  }

  downloadGuideImport() {
    this.documentSupportService.downloadGuideImport().subscribe(data => {
      switch (data.type) {
        case HttpEventType.Response: {
          const downloadedFile = new Blob([data.body!], { type: data.body!.type });
          const a = document.createElement('a');
          a.setAttribute('style', 'display:none;');
          document.body.appendChild(a);
          a.download = "GuiaDocumentoSoporte.docx";
          a.href = URL.createObjectURL(downloadedFile);
          a.target = '_blank';
          a.click();
          document.body.removeChild(a);
          break;
        }
      }
    });
  }

  async getAllLists(...listsToLoad: Array<'companies' | 'branches' | 'enterprises' | 'articles'>) {
    try {
      const lists = {
        companies: async () => {
          this.listCompanies = await firstValueFrom(this.companyService.getAllCompaniesForSelectAsync());
        },
        branches: async () => {
          this.listBranches = await firstValueFrom(this.branchService.GetAllBranchesByCompanyIdForSelectAsync(this.companyId));
        },
        enterprises: async () => {
          this.listEnterprises = await firstValueFrom(this.enterpriseService.getEnterprisesByBranchIdForSelect(this.branchId));
        },
        articles: async () => {
          this.listArticles = await firstValueFrom(this.articleService.getAllArticlesByCompanyForSelectAsync(this.companyId));
        }

      };
      if (listsToLoad.length) {
        await Promise.all(listsToLoad.map(list => lists[list]()));
      } else {
        await Promise.all([
          lists.branches(),
          lists.companies(),
          lists.enterprises(),
          lists.articles()
        ]);
      }
    } catch (error) {
      console.error('Error obtener las listas.', error);
    }
  }

  async getBranch(branchId: number | null) {
    if (!branchId) {
      this.branchId = 0;
      this.branchCode = 0;
      this.listEnterprises = [];
      return;
    }

    const branch = this.listBranches.find(x => x.value == branchId);
    this.branchId = branchId;
    this.branchCode = branch.code;
    await this.getAllLists('enterprises');
  }

  async getCompany(companyId: number | null) {
    if (!companyId) {
      this.companyId = 0;
      this.companyCode = 0;
      this.listArticles = [];
      this.listBranches = [];
      this.listEnterprises = [];
      this.validateForm();
      return;
    }

    const company = this.listCompanies.find(x => x.value == companyId);
    this.companyId = companyId;
    this.companyCode = company.code;
    this.listArticles = [];
    this.formImport.get('articleId')?.patchValue(null);
    await this.getAllLists('branches', 'articles');
  }

  async getEnterprise(enterpriseId: number | null) {
    if (!enterpriseId) {
      this.enterpriseId = 0;
      this.enterpriseCode = 0;
      return;
    }

    const enterprise = this.listEnterprises.find(x => x.value == enterpriseId);
    this.enterpriseId = enterpriseId;
    this.enterpriseCode = enterprise.code;
  }

  getNameFile(file: any) {
    this.eventFile = file;
    const files = this.eventFile.target.files;
    if (files.length != 0) {
      for (let i = 0; i < files.length; i++) {
        this.nameFile = files[i].name;
      }
    } else {
      this.nameFile = 'Ningún archivo seleccionado.';
    }
  }

  async getPermission() {
    let url = this.router.url as string
    url = url.substring(1);
    this.url = url;
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await Promise.all([
      this.getAllLists('companies'),
      this.getPermissionProfileByUrl(),
    ])
  }

  async getPermissionProfileByUrl() {
    try {
      const response = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
      this.permissionUpdatable = response;
    } catch (error) {
      console.error('Error al buscar los permisos.', error);
    }
  }

  importDocumentsSupport() {
    this.documentSupportsAlerts = [];
    this.errorNotes = 0;
    this.visibleTxt = false;
    if (!this.formImport.valid) {
      this.showValidationMessages();
      return;
    }
    let isOk: boolean = true;
    const input = document.getElementById("fileImport") as any;
    const rowsImports = new Array<DocumentSupportImportModel>();
    let row: DocumentSupportImportModel;
    let counter: number = 1, bussinessDocumentsTypeCode: number, consecutive: number, dateDocumentSupport: Date, providerDocument: string, cuds: string | null,
      totalCreditNoteValue: number, residueValue: number, totalValue: number, articleCode: number, quantity: number, unitValue: number, creditNotesValue: number;
    if (input.files[0]) {
      readXlsxFile(input.files[0]).then(async (rows) => {
        if (rows.length <= 1) {
          this.toastHandlerService.showToast(
            'warning',
            `El archivo ingresado no tiene registros para importar`
          );
          return;
        }
        rows.splice(0, 1);
        rows.forEach(element => {
          counter = counter + 1;
          //A. Código Compañía:
          const companyCode = parseFloat(element[0] as string);
          if (isNaN(companyCode)) {
            this.toastHandlerService.showToast(
              'warning',
              `Columna A, Fila ${counter}. El código de la compañía no es válido.`
            );
            isOk = false;
            this.clearInputFile();
            return;
          }
          if (companyCode != this.companyCode) {
            this.toastHandlerService.showToast(
              'warning',
              `Columna A, Fila ${counter}. La compañía del archivo no es igual a la compañía seleccionada.`
            );
            isOk = false;
            this.clearInputFile();
            return;
          }

          //B. Código Sucursal:
          const branchCode = parseFloat(element[1] as string);
          if (isNaN(branchCode)) {
            this.toastHandlerService.showToast(
              'warning',
              `Columna B, Fila ${counter}. El código de la sucursal no es válido.`
            );
            isOk = false;
            this.clearInputFile();
            return;
          }
          if (branchCode != this.branchCode) {
            this.toastHandlerService.showToast(
              'warning',
              `Columna B, Fila ${counter}. La sucursal del archivo no es igual a la sucursal seleccionada.`
            );
            isOk = false;
            this.clearInputFile();
            return;
          }

          //C. Código Empresa:
          const enterpriseCode = parseFloat(element[2] as string);
          if (isNaN(enterpriseCode)) {
            this.toastHandlerService.showToast(
              'warning',
              `Columna C, Fila ${counter}. El código de la empresa no es válido.`
            );
            isOk = false;
            this.clearInputFile();
            return;
          }
          if (enterpriseCode != this.enterpriseCode) {
            this.toastHandlerService.showToast(
              'warning',
              `Columna C, Fila ${counter}. La empresa del archivo no es igual a la empresa seleccionada.`
            );
            isOk = false;
            this.clearInputFile();
            return;
          }

          //D. Tipo de Documento Comercial:
          bussinessDocumentsTypeCode = parseFloat(element[3] as string);
          if (isNaN(bussinessDocumentsTypeCode)) {
            this.toastHandlerService.showToast(
              'warning',
              `Columna D, Fila ${counter}. El tipo de documento comercial no es válido.`
            );
            isOk = false;
            this.clearInputFile();
            return;
          }

          //E. Número de Documento de Soporte:
          consecutive = parseFloat(element[4] as string);
          if (isNaN(consecutive)) {
            this.toastHandlerService.showToast(
              'warning',
              `Columna E, Fila ${counter}. El número de Documento de Soporte no es válido.`
            );
            isOk = false;
            this.clearInputFile();
            return;
          }

          //F. Fecha:
          const dateDocumentSupportString = element[5] as string;
          if (dateDocumentSupportString == null || dateDocumentSupportString == "") {
            this.toastHandlerService.showToast(
              'warning',
              `Columna F, Fila ${counter}. El campo de fecha del Documento de Soporte es obligatorio.`
            );
            isOk = false;
            this.clearInputFile();
            return;
          }
          //Convierte fecha de string a Date
          dateDocumentSupport = moment(dateDocumentSupportString, 'DD/MM/YYYY').toDate();
          if (!moment(dateDocumentSupportString, 'DD/MM/YYYY').isValid()) {
            this.toastHandlerService.showToast(
              'warning',
              `Columna F, Fila ${counter}. El campo de fecha del Documento de Soporte no es válido.`
            );
            isOk = false;
            this.clearInputFile();
            return;
          }

          //G. Documento Proveedor:
          providerDocument = element[6] as string;
          if (providerDocument == null) {
            this.toastHandlerService.showToast(
              'warning',
              `Columna G, Fila ${counter}. El documento del proveedor no es válido.`
            );
            isOk = false;
            this.clearInputFile();
            return;
          }
          if (providerDocument.length > 15) {
            this.toastHandlerService.showToast(
              'warning',
              `Columna G, Fila ${counter}. El documento del proveedor supera el tamaño máximo de caracteres (15)`
            );
            isOk = false;
            this.clearInputFile();
            return;
          }

          //H. Cuds:
          cuds = element[7] as string;
          if (cuds != null) {
            if (cuds.length > 99) {
              this.toastHandlerService.showToast(
                'warning',
                `Columna H, Fila ${counter}. El cuds supera el tamaño máximo de caracteres (99).`
              );
              isOk = false;
              this.clearInputFile();
              return;
            }
          }

          //I. Valor Total Nota Crédito:
          const valueTotalCreditNote = element[8];
          if (valueTotalCreditNote == null || valueTotalCreditNote === "") {
            totalCreditNoteValue = 0;
          } else {
            totalCreditNoteValue = Number(valueTotalCreditNote);
            if (isNaN(totalCreditNoteValue)) {
              this.toastHandlerService.showToast(
                'warning',
                `Columna I, Fila ${counter}. El Valor total nota crédito no es válido.`
              );
              isOk = false;
              this.clearInputFile();
              return;
            }
          }

          //J. Valor Saldo:
          const valueResidue = element[9];
          if (valueResidue == null || valueResidue === "") {
            residueValue = 0;
          } else {
            residueValue = Number(valueResidue);
            if (isNaN(residueValue)) {
              this.toastHandlerService.showToast(
                'warning',
                `Columna J, Fila ${counter}. El Valor saldo no es válido.`
              );
              isOk = false;
              this.clearInputFile();
              return;
            }
          }

          //K. Valor Total:
          const valueTotal = element[10];
          if (valueTotal == null || valueTotal === "") {
            totalValue = 0;
          } else {
            totalValue = Number(valueTotal);
            if (isNaN(totalValue)) {
              this.toastHandlerService.showToast(
                'warning',
                `Columna K, Fila ${counter}. El Valor total no es válido.`
              );
              isOk = false;
              this.clearInputFile();
              return;
            }
          }

          //L. Código Artículo:
          const valueArticleCode = element[11];
          if (valueArticleCode == null || valueArticleCode === "") {
            this.toastHandlerService.showToast(
              'warning',
              `Columna L, Fila ${counter}. El código del artículo no es válido.`
            );
            isOk = false;
            this.clearInputFile();
            return;
          }
          articleCode = Number(valueArticleCode);
          if (isNaN(articleCode)) {
            this.toastHandlerService.showToast(
              'warning',
              `Columna L, Fila ${counter}. El código del artículo no es válido.`
            );
            isOk = false;
            this.clearInputFile();
            return;
          }

          //M. Cantidad
          const valueQuantity = element[12];
          if (valueQuantity == null || valueQuantity === "") {
            this.toastHandlerService.showToast(
              'warning',
              `Columna M, Fila ${counter}. La cantidad no es válida.`
            );
            isOk = false;
            this.clearInputFile();
            return;
          }
          quantity = Number(valueQuantity);
          if (isNaN(quantity)) {
            this.toastHandlerService.showToast(
              'warning',
              `Columna M, Fila ${counter}. La cantidad no es válida.`
            );
            isOk = false;
            this.clearInputFile();
            return;
          }

          //N. Valor Unitario:
          const valueInitialValue = element[13];
          if (valueInitialValue == null || valueInitialValue === "") {
            unitValue = 0;
          } else {
            unitValue = Number(valueInitialValue);
            if (isNaN(unitValue)) {
              this.toastHandlerService.showToast(
                'warning',
                `Columna N, Fila ${counter}. El Valor unitario no es válido.`
              );
              isOk = false;
              this.clearInputFile();
              return;
            }
          }

          //O. Valor Nota Crédito:
          const valuecreditNotes = element[14];
          if (valuecreditNotes == null || valuecreditNotes === "") {
            creditNotesValue = 0;
          } else {
            creditNotesValue = Number(valuecreditNotes);
            if (isNaN(creditNotesValue)) {
              this.toastHandlerService.showToast(
                'warning',
                `Columna O, Fila ${counter}. El Valor nota crédito no es válido.`
              );
              isOk = false;
              this.clearInputFile();
              return;
            }
          }

          row = new DocumentSupportImportModel(counter, this.companyId!, this.branchId, this.enterpriseId, bussinessDocumentsTypeCode,
            consecutive, dateDocumentSupport, providerDocument, cuds, totalCreditNoteValue, residueValue, totalValue, articleCode,
            quantity, unitValue, creditNotesValue);
          rowsImports.push(row);
        });
        if (isOk && rowsImports.length > 0) {
          try {
            const response = await firstValueFrom(this.documentSupportService.importDocumentSupport(rowsImports));
            this.loadPreInvoicingModel = response;
            this.documentSupportsAlerts = response?.alerts ?? [];
            this.errorNotes = response?.preInvoicingStateId;

            if (this.errorNotes === 1) {
              this.visibleTxt = true;
              this.toastHandlerService.showToast(
                'error',
                'La importación terminó con errores, puede exportarlos si lo desea'
              );
            } else if (this.documentSupportsAlerts.length > 0) {
              this.visibleTxt = true;
              this.toastHandlerService.showToast(
                'error',
                'La importación terminó con alertas, puede exportarlas si lo desea'
              );
            } else {
              this.visibleTxt = false;
              this.toastHandlerService.showToast(
                'success',
                `La importación terminó exitosamente.`
              );
            }
            this.clearInputFile();
          } catch (error) {
            this.nameFile = 'Ningún archivo seleccionado.';
            console.error('Error al importar documentos de soporte.', error);
          }
          (<HTMLInputElement>document.getElementById('fileImport')).value = '';
        }
      });
    } else {
      this.toastHandlerService.showToast(
        'error',
        'No ha seleccionado ningún archivo.'
      );
    }

  }

  openToggle(event: any) {
    this.informationPopover.toggle(event);
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      companyId: {
        required: 'La compañía es obligatoria.',
        min: 'La compañía es obligatiria.',
      },
      branchId: {
        required: 'La sucursal es obligatoria.',
        min: 'La sucursal es obligatoria.'
      },
      enterpriseId: {
        required: 'La empresa es obligatoria.',
        min: 'La empresa es obligatoria.'
      },
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formImport.get(controlName);
      if (control && control.invalid && control.errors) {
        const errors = control.errors;
        Object.keys(errors).forEach(errorKey => {
          const message = controlMessages[controlName][errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  validateForm() {
    this.formImport = this.formBuilder.group({
      companyId: [0, [Validators.required, Validators.min(1)]],
      branchId: [0, [Validators.required, Validators.min(1)]],
      enterpriseId: [0, [Validators.required, Validators.min(1)]],
    });
  }
}
